import { Fragment, useMemo, useRef, useState } from "react";
import { Combobox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/24/solid";
import useDropdownPosition from "../../utils/useDropdownPosition";

interface LabelValueItem<T> {
  label: string;
  value: T;
}

interface SearchableDropdownProps<T, U extends boolean = false> {
  items: U extends true ? LabelValueItem<T>[] : T[];
  selectedItem: T | undefined;
  setSelectedItem: (value: T | undefined) => void;
  outerClassName?: string;
  text?: string;
  label?: string;
  disabled?: boolean;
  displayValue: (item: T | undefined) => string;
  filterFunction?: (item: U extends true ? LabelValueItem<T> : T, query: string) => boolean;
  sortFunction?: (a: U extends true ? LabelValueItem<T> : T, b: U extends true ? LabelValueItem<T> : T) => number;
  placeholder?: string;
  useLabelAndValue?: U;
}

const SearchableDropdown = <T, U extends boolean = false>({
  items,
  selectedItem,
  setSelectedItem,
  outerClassName = '',
  label,
  disabled,
  displayValue,
  filterFunction,
  sortFunction,
  placeholder,
  useLabelAndValue = false as U,
}: SearchableDropdownProps<T, U>) => {
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);

  const position = useDropdownPosition(containerRef);

  const filteredItems = useMemo(() => {
    if (query === "") {
      return items;
    }

    const filtered = items.filter((item) =>
      filterFunction ? filterFunction(item as (U extends true ? LabelValueItem<T> : T), query) : true
    );

    return sortFunction ? filtered.sort((a, b) => (sortFunction as (a: T | LabelValueItem<T>, b: T | LabelValueItem<T>) => number)(a, b)) : filtered;
  }, [query, items, filterFunction, sortFunction]);

  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <div className={`w-full relative ${outerClassName}`} ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <Combobox
        value={selectedItem}
        onChange={setSelectedItem}
        disabled={disabled ?? false}
      >
        {({ open }) => (
          <div className="relative w-full">
            <div
              className={`relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-blue-300 sm:text-sm border border-gray-300 flex flex-row items-center`}
            >
              <Combobox.Input
                className={`w-full border-none py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0 h-full rounded-md border-blue-600`}
                displayValue={displayValue}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={placeholder}
                onClick={() => {
                  if (!open) {
                    buttonRef.current?.click();
                  }
                }}
              />
              <Combobox.Button
                className="absolute inset-y-0 right-0 flex items-center pr-2"
                ref={buttonRef}
              >
                <ChevronUpDownIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </Combobox.Button>
            </div>
            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div
                ref={optionsRef}
                className={`absolute max-h-40 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm regular-scrollbar z-[999] ${
                  position === 'above' ? 'bottom-full mb-2' : 'mt-2'
                }`}
              >
                <Combobox.Options static>
                  {filteredItems.length === 0 && query !== "" ? (
                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                      Nothing found.
                    </div>
                  ) : (
                    filteredItems.map((item) => (
                      <Combobox.Option
                        key={JSON.stringify(item)}
                        className={({ active }) =>
                          `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                            active
                              ? "bg-accent text-main-text-darker"
                              : "text-gray-900"
                          }`
                        }
                        value={useLabelAndValue ? (item as LabelValueItem<T>).value : item}
                        onClick={() => setQuery("")}
                      >
                        {({ selected, active }) => (
                          <>
                            <span
                              className={`block truncate ${
                                selected ? "font-medium" : "font-normal"
                              }`}
                            >
                              {useLabelAndValue ? (item as LabelValueItem<T>).label : displayValue(item as T)}
                            </span>
                            {selected && (
                              <span
                                className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                  active ? "text-white" : "text-blue-600"
                                }`}
                              >
                                <CheckIcon
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              </span>
                            )}
                          </>
                        )}
                      </Combobox.Option>
                    ))
                  )}
                </Combobox.Options>
              </div>
            </Transition>
          </div>
        )}
      </Combobox>
    </div>
  );
};

export default SearchableDropdown;